import HardBreak from "@tiptap/extension-hard-break"
import type {
  Mark as ProsemirrorMark,
  Node as ProsemirrorNode,
} from "@tiptap/pm/model"

export const NodeUtils = {
  isHardBreak(node: ProsemirrorNode) {
    return node.type.name === HardBreak.name
  },

  children: {
    reduce<T>(
      node: ProsemirrorNode,
      f: (acc: T, child: ProsemirrorNode, index: number) => T,
      defaultValue: T,
    ) {
      let accumulator: T = defaultValue
      node.forEach((child, _offset, index) => {
        accumulator = f(accumulator, child, index)
      })
      return accumulator
    },

    map<T, TReturnReadOnly extends boolean = true>(
      node: ProsemirrorNode,
      f: (child: ProsemirrorNode) => T,
    ): TReturnReadOnly extends true ? readonly T[] : T[] {
      return this.reduce<T[]>(
        node,
        (acc, child) => {
          return [...acc, f(child)]
        },
        [],
      ) as TReturnReadOnly extends true ? readonly T[] : T[]
    },

    exists(node: ProsemirrorNode, f: (child: ProsemirrorNode) => boolean) {
      return this.reduce(
        node,
        (acc, child) => {
          return acc || f(child)
        },
        false,
      )
    },

    filter(
      node: ProsemirrorNode,
      predicate: (child: ProsemirrorNode) => boolean,
    ): readonly ProsemirrorNode[] {
      return this.reduce<ProsemirrorNode[]>(
        node,
        (acc, child) => {
          return [...acc, ...(predicate(child) ? [child] : [])]
        },
        [],
      ) as readonly ProsemirrorNode[]
    },
  },

  marks: {
    reduce<T>(
      node: ProsemirrorNode,
      f: (acc: T, child: ProsemirrorMark, index: number) => T,
      defaultValue: T,
    ): T {
      let accumulator: T = defaultValue
      node.marks.forEach((child, index) => {
        accumulator = f(accumulator, child, index)
      })
      return accumulator
    },

    map<T>(
      node: ProsemirrorNode,
      f: (mark: ProsemirrorMark) => T,
    ): readonly T[] {
      return this.reduce<T[]>(
        node,
        (acc, mark) => {
          return [...acc, f(mark)]
        },
        [],
      ) as readonly T[]
    },

    filter(
      node: ProsemirrorNode,
      predicate: (mark: ProsemirrorMark) => boolean,
    ): readonly ProsemirrorMark[] {
      return this.reduce<ProsemirrorMark[]>(
        node,
        (acc, mark) => {
          return [...acc, ...(predicate(mark) ? [mark] : [])]
        },
        [],
      ) as readonly ProsemirrorMark[]
    },
  },
}
